/**
 * 替换模板中的占位图片
 * 1. 查找模板中所有使用 Unsplash 或占位图片的元素
 * 2. 根据 alt 描述使用 image-generator 生成图片
 * 3. 使用 image-uploader 上传到 CDN
 * 4. 替换模板中的 URL
 */

export interface ImageInfo {
  /** 图片路径（在模板中的位置） */
  path: string;
  /** 原始 URL */
  originalSrc: string;
  /** 图片描述（用于生成） */
  alt: string;
  /** 宽度 */
  width?: string;
}

export interface ExtractImagesInput {
  template: any;
}

export interface ExtractImagesOutput {
  success: boolean;
  images?: ImageInfo[];
  error?: string;
}

export interface ReplaceImagesInput {
  template: any;
  replacements: Array<{
    path: string;
    newSrc: string;
  }>;
}

export interface ReplaceImagesOutput {
  success: boolean;
  template?: any;
  replacedCount?: number;
  error?: string;
}

/**
 * 检查 URL 是否是占位图片
 */
function isPlaceholderImage(src: string): boolean {
  if (!src) return false;
  const placeholderPatterns = [
    "unsplash.com",
    "placeholder.com",
    "via.placeholder",
    "placekitten.com",
    "picsum.photos",
    "loremflickr.com",
    "placehold.it",
    "dummyimage.com",
    "fakeimg.pl",
    "[图片",
    "[Image",
    "example.com",
  ];
  return placeholderPatterns.some((pattern) =>
    src.toLowerCase().includes(pattern.toLowerCase())
  );
}

/**
 * 递归遍历模板，提取所有图片信息
 */
function extractImagesRecursive(
  node: any,
  path: string,
  images: ImageInfo[]
): void {
  if (!node || typeof node !== "object") return;

  // 检查是否是图片元素
  if (node.type === "standard-image" || node.type === "image") {
    const src = node.attributes?.src || "";
    const alt = node.attributes?.alt || "";
    const width = node.attributes?.width;

    if (src && (isPlaceholderImage(src) || alt)) {
      images.push({
        path,
        originalSrc: src,
        alt: alt || "A professional email marketing image",
        width,
      });
    }
  }

  // 检查 Hero 背景图
  if (
    (node.type === "standard-hero" || node.type === "hero") &&
    node.attributes?.["background-url"]
  ) {
    const src = node.attributes["background-url"];
    if (isPlaceholderImage(src)) {
      images.push({
        path: `${path}.attributes.background-url`,
        originalSrc: src,
        alt: node.attributes?.alt || "Hero background image",
        width: node.attributes?.width,
      });
    }
  }

  // 检查 Section 背景图
  if (
    (node.type === "standard-section" || node.type === "section") &&
    node.attributes?.["background-url"]
  ) {
    const src = node.attributes["background-url"];
    if (isPlaceholderImage(src)) {
      images.push({
        path: `${path}.attributes.background-url`,
        originalSrc: src,
        alt: "Section background image",
        width: undefined,
      });
    }
  }

  // 递归处理 children
  if (Array.isArray(node.children)) {
    node.children.forEach((child: any, index: number) => {
      extractImagesRecursive(child, `${path}.children[${index}]`, images);
    });
  }
}

/**
 * 提取模板中的所有占位图片
 */
export function extractPlaceholderImages(
  input: ExtractImagesInput
): ExtractImagesOutput {
  try {
    const { template } = input;

    if (!template) {
      throw new Error("Template is required");
    }

    const images: ImageInfo[] = [];
    extractImagesRecursive(template, "template", images);

    return {
      success: true,
      images,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

/**
 * 根据路径获取对象的值
 */
function getByPath(obj: any, path: string): any {
  const parts = path.replace(/\[(\d+)\]/g, ".$1").split(".");
  let current = obj;
  for (const part of parts) {
    if (current === undefined || current === null) return undefined;
    current = current[part];
  }
  return current;
}

/**
 * 根据路径设置对象的值
 */
function setByPath(obj: any, path: string, value: any): void {
  const parts = path.replace(/\[(\d+)\]/g, ".$1").split(".");
  let current = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    if (current[part] === undefined) {
      current[part] = {};
    }
    current = current[part];
  }
  current[parts[parts.length - 1]] = value;
}

/**
 * 替换模板中的图片 URL
 */
export function replaceImageUrls(
  input: ReplaceImagesInput
): ReplaceImagesOutput {
  try {
    const { template, replacements } = input;

    if (!template) {
      throw new Error("Template is required");
    }

    if (!Array.isArray(replacements)) {
      throw new Error("Replacements must be an array");
    }

    // 深拷贝模板
    const newTemplate = JSON.parse(JSON.stringify(template));

    let replacedCount = 0;

    for (const { path, newSrc } of replacements) {
      // 处理不同的路径格式
      if (path.endsWith(".attributes.background-url")) {
        // 背景图路径
        const basePath = path.replace(".attributes.background-url", "");
        const relativePath = basePath.replace("template.", "");
        const node = relativePath
          ? getByPath(newTemplate, relativePath)
          : newTemplate;
        if (node?.attributes) {
          node.attributes["background-url"] = newSrc;
          replacedCount++;
        }
      } else {
        // 普通图片元素路径
        const relativePath = path.replace("template.", "");
        const node = relativePath
          ? getByPath(newTemplate, relativePath)
          : newTemplate;
        if (node?.attributes) {
          node.attributes.src = newSrc;
          replacedCount++;
        }
      }
    }

    return {
      success: true,
      template: newTemplate,
      replacedCount,
    };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
    };
  }
}

export const EXTRACT_PLACEHOLDER_IMAGES_TOOL = {
  name: "extract_placeholder_images",
  description: `提取模板中的所有占位图片（Unsplash、placeholder.com 等）。

返回每个图片的:
- path: 在模板中的路径
- originalSrc: 原始 URL
- alt: 图片描述（用于生成新图片）
- width: 图片宽度

用法:
1. 调用此工具提取所有占位图片
2. 对每个图片，使用 image-generator 根据 alt 描述生成图片
3. 使用 image-uploader 上传到 CDN
4. 调用 replace_image_urls 替换模板中的 URL`,
  inputSchema: {
    type: "object" as const,
    properties: {
      template: {
        type: "object",
        description: "邮件模板 JSON",
      },
    },
    required: ["template"],
  },
};

export const REPLACE_IMAGE_URLS_TOOL = {
  name: "replace_image_urls",
  description: `替换模板中的图片 URL。

输入:
- template: 邮件模板 JSON
- replacements: 替换列表，每项包含:
  - path: 图片在模板中的路径（从 extract_placeholder_images 获取）
  - newSrc: 新的图片 URL

返回:
- template: 替换后的模板
- replacedCount: 替换的图片数量`,
  inputSchema: {
    type: "object" as const,
    properties: {
      template: {
        type: "object",
        description: "邮件模板 JSON",
      },
      replacements: {
        type: "array",
        description: "替换列表",
        items: {
          type: "object",
          properties: {
            path: { type: "string" },
            newSrc: { type: "string" },
          },
          required: ["path", "newSrc"],
        },
      },
    },
    required: ["template", "replacements"],
  },
};
